import React, { useEffect } from "react";
import { IconRotate, IconX, IconCheck } from "@tabler/icons-react";
import { FILTER_TITLES } from "../utils/jobsConstants";

/**
 * A single filter group inside the mobile drawer: an uppercase section title
 * followed by divider-separated rows (checkbox + label + count). Multi-select.
 */
const DrawerGroup = ({ title, options, selected, onToggle }) => {
  if (!options || options.length === 0) return null;

  return (
    <div className="jobsDrawerGroup">
      <div className="jobsDrawerSectionHeader">
        <p className="jobsDrawerSectionTitle">{title}</p>
      </div>
      <div className="jobsDrawerList">
        {options.map((option, index) => {
          const isChecked = selected.includes(option.value);
          const isLast = index === options.length - 1;
          return (
            <div className="jobsDrawerRowWrap" key={option.value}>
              <button
                type="button"
                className={`jobsDrawerRow${isChecked ? " isChecked" : ""}`}
                onClick={() => onToggle(option.value)}
                aria-pressed={isChecked}
              >
                <span className="jobsDrawerCheckbox" aria-hidden="true">
                  {isChecked && <IconCheck size={12} stroke={3} />}
                </span>
                <span className="jobsDrawerLabel">{option.label}</span>
                <span className="jobsDrawerCount">{option.count}</span>
              </button>
              {!isLast && <div className="jobsDivider" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Mobile filter drawer (Figma: job-listings-filter-view). Slides in over the
 * page and holds the Location/Department filters that live in the sidebar on
 * desktop. Filters apply live; "Apply Filters" simply closes the drawer.
 */
const FilterDrawer = ({
  open,
  onClose,
  locationOptions,
  selectedLocations,
  onToggleLocation,
  departmentOptions,
  selectedDepartments,
  onToggleDepartment,
  hasActiveFilters,
  onReset,
}) => {
  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="jobsDrawerOverlay" onClick={onClose}>
      <div
        className="jobsDrawerPanel"
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="jobsDrawerScroll">
          <div className="jobsDrawerHeader">
            <p className="jobsDrawerTitle">Filters</p>
            <div className="jobsDrawerHeaderActions">
              {hasActiveFilters && (
                <button
                  type="button"
                  className="jobsResetLink"
                  onClick={onReset}
                >
                  <IconRotate size={16} stroke={2} />
                  Reset filters
                </button>
              )}
              <button
                type="button"
                className="jobsDrawerClose"
                onClick={onClose}
                aria-label="Close filters"
              >
                <IconX size={16} stroke={2} />
              </button>
            </div>
          </div>

          <div className="jobsDivider" />

          <div className="jobsDrawerBody">
            <DrawerGroup
              title={FILTER_TITLES.location}
              options={locationOptions}
              selected={selectedLocations}
              onToggle={onToggleLocation}
            />
            <DrawerGroup
              title={FILTER_TITLES.department}
              options={departmentOptions}
              selected={selectedDepartments}
              onToggle={onToggleDepartment}
            />
          </div>
        </div>

        <div className="jobsDrawerFooter">
          <button type="button" className="jobsApplyBtn" onClick={onClose}>
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterDrawer;
